import React, { useEffect, useRef, useState } from 'react'
import { apiJson, ApiError } from '../../lib/api'
import { offsetText, zoneOptions, ZoneNow } from '../../lib/timezones'
import { useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'
import { plural } from '../wages/format'
import pj from '../PickingJobsPage.module.css'
import sp from '../SitesPage.module.css'
import o from '../../components/Overlay.module.css'
import m from './SiteMenu.module.css'
import { liveVarieties, type SiteVariety } from './structure'

// The one shared Site selector (§7g), used by Structure and Picking jobs:
// every Site is listed, an archived one marked rather than hidden, and a
// new Site is added from the menu itself. The cog beside it opens the Site's
// settings (PUT /api/sites/:id, routes/sites.js).

export interface SiteSummary {
  id: string
  name: string
  address?: string | null
  // NUMERIC(10,2): the API sends it as text.
  size_hectares?: number | string | null
  // The Site's own zone; null inherits the tenant's (077, SITE-2).
  timezone?: string | null
  // The zone its captures are read in: its own, else the tenant's.
  effective_timezone?: string
  archived_at?: string | null
  // Where the Site is, in words (083): display text only.
  region?: string | null
  // $/kg a new picking job here starts with (137); null is no default.
  default_rate_per_kg?: number | null
}

export interface MapCentre {
  lat: number
  lng: number
}

export interface SiteRecord extends SiteSummary {
  address: string | null
  size_hectares: number | string | null
  timezone: string | null
  // GET /api/sites/:id only: what it would inherit, and its zone now.
  tenant_timezone?: string | null
  zone?: ZoneNow | null
  grower_pin: string | null
  estimated_item_weight_g: number
  // The fruit in a full bin, for a shared bin in Self Scan (198); 0 is not set. Older APIs leave it out.
  estimated_bin_weight_g?: number
  level1_label: string
  level2_label: string
  level3_label: string
  show_level2: boolean
  map_level: number
  // Where the Site's map opens (083); null is not set, never a guess.
  map_centre?: MapCentre | null
  map_zoom?: number | null
  archived_at: string | null
  // GET /api/sites/:id only: work is recorded here, so the time zone is fixed.
  timezone_locked?: boolean
}

// GET /api/sites/:id/mapping-impact (routes/structure.js).
export interface MappingImpact {
  level: number
  current_level: number
  boundaries: number
  targets: number
}

// What a mapping-level change does, said before it is saved. L is the level
// names as the form holds them.
export function mappingImpactText(i: MappingImpact, L: string[]): string {
  const to = L[i.level - 1].toLowerCase()
  const from = L[i.current_level - 1].toLowerCase()
  const go = i.boundaries
    ? `Changing the mapping level replaces the map: the ${plural(i.boundaries, `${from} boundary`, `${from} boundaries`)} drawn now will be removed from attribution. Their shapes stay on record.`
    : `No ${from} boundary is drawn yet, so nothing is removed.`
  const draw = i.targets ? ` The new level has ${plural(i.targets, to, `${to}s`)} to draw, each unmapped until it is.` : ` There is no active ${to} to draw yet.`
  return `${go}${draw} Nothing changes until you save.`
}

const err = (e: any, fallback: string) => (e?.message as string) || fallback
export const hectares = (v: SiteSummary['size_hectares']) => (v === null || v === undefined || v === '' ? null : Number(v))
const haText = (v: SiteSummary['size_hectares']) => {
  const n = hectares(v)
  return n ? `${n.toLocaleString('en-NZ', { maximumFractionDigits: 2 })} ha` : null
}

// A menu row's second line, as V4 writes it: the region where one is set,
// and the time zone only where the Site has its own (§7g, "its time zone
// where one is set").
export const siteLine = (s: SiteSummary) =>
  `${s.archived_at ? 'Archived · ' : ''}${s.region ? `${s.region} · ` : ''}${haText(s.size_hectares) || 'Size not set'}${s.timezone ? ` · ${s.timezone}` : ''}`

export const addedToast = (name: string) => `${name} added. Set its address, size, grower PIN and levels in site settings.`

// A new Site inherits the tenant's zone. While the tenant has none, the API
// refuses (TENANT_TIMEZONE_UNSET) rather than start it on UTC, and the form
// asks for the Site's own zone instead of guessing one from the browser.
export function AddSiteForm({ user, onAdded, className }: { user: any; onAdded: (s: SiteRecord) => void; className?: string }) {
  const [name, setName] = useState('')
  const [needZone, setNeedZone] = useState(false)
  const [zone, setZone] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const ready = !!name.trim() && (!needZone || !!zone)
  const submit = async () => {
    if (busy || !ready) return
    setBusy(true)
    setError('')
    try {
      const body = needZone ? { name: name.trim(), timezone: zone } : { name: name.trim() }
      const s = await apiJson<SiteRecord>(user, '/api/sites', { method: 'POST', body })
      setName('')
      setNeedZone(false)
      setZone('')
      onAdded(s)
    } catch (e: any) {
      if (e instanceof ApiError && e.body?.code === 'TENANT_TIMEZONE_UNSET') setNeedZone(true)
      setError(err(e, 'The site was not added'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className={className}>
      <div className={m.add}>
        <input
          className={m.addInput}
          placeholder="New site name…"
          aria-label="New site name"
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
        />
        <PendingButton type="button" className={m.addBtn} busy={busy} pending="Creating…" disabled={!ready} onClick={submit}>
          + Add site
        </PendingButton>
      </div>
      {needZone && (
        <select className={m.addInput} aria-label="New site time zone" value={zone} onChange={(e) => setZone(e.target.value)}>
          <option value="">Choose this site's time zone…</option>
          {zoneOptions().map((z) => (
            <option key={z} value={z}>
              {z}
            </option>
          ))}
        </select>
      )}
      <Failed text={error} />
    </div>
  )
}

function Gear() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M9.3 1l.4 1.8c.4.1.8.3 1.1.5l1.6-1 1.3 1.3-1 1.6c.2.3.4.7.5 1.1L15 6.7v1.9l-1.8.4c-.1.4-.3.8-.5 1.1l1 1.6-1.3 1.3-1.6-1c-.3.2-.7.4-1.1.5L9.3 14.3H7.4L7 12.5c-.4-.1-.8-.3-1.1-.5l-1.6 1L3 11.7l1-1.6c-.2-.3-.4-.7-.5-1.1L1.7 8.6V6.7l1.8-.4c.1-.4.3-.8.5-1.1l-1-1.6L4.3 2.3l1.6 1c.3-.2.7-.4 1.1-.5L7.4 1h1.9zM8.35 5.2a2.45 2.45 0 1 0 0 4.9 2.45 2.45 0 0 0 0-4.9z"
      />
    </svg>
  )
}

// A variety is chosen from the Site's live list, never typed: "Import must
// never create an unknown variety", and manual forms run the same rules
// (§7g). A name the record already carries that has since been archived is
// still shown, as the current value only.
export function VarietySelect({
  id,
  list,
  value,
  onChange,
  empty = 'Choose a variety',
  current,
  disabled,
  className = pj.input,
}: {
  id: string
  list: SiteVariety[]
  value: string
  onChange: (v: string) => void
  empty?: string
  current?: string | null
  disabled?: boolean
  className?: string
}) {
  const live = liveVarieties(list)
  const kept = current && !live.includes(current) ? current : null
  return (
    <>
      <select id={id} className={className} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        {!kept && <option value="">{live.length ? empty : 'No varieties on this Site yet'}</option>}
        {kept && (
          <option value={kept}>
            {kept} ({list.some((v) => v.name === kept) ? 'archived' : 'not on the list'})
          </option>
        )}
        {live.map((v) => (
          <option key={v} value={v}>
            {v}
          </option>
        ))}
      </select>
      {!live.length && <span className={pj.note}>Add the Site's varieties from the Site menu, under Varieties.</span>}
    </>
  )
}

// The Site's variety list (§7g "Excel import": "Varieties (protected current
// reference list with stable identifiers, feeding the dropdown)"; §9.2
// "Varieties available per site"). Every row, bulk edit, replant and job
// picks its variety from here. A name is never edited, because rows, jobs
// and what follows them carry it: a mistyped one is archived and the right
// one added. Archiving takes it off the list for new work only.
export function VarietiesDialog({ user, siteId, onClose, onChanged }: { user: any; siteId: string; onClose: () => void; onChanged?: () => void }) {
  const [site, setSite] = useState<{ id: string; name: string; archived_at: string | null } | null>(null)
  const [list, setList] = useState<SiteVariety[] | null>(null)
  const [name, setName] = useState('')
  const [showArchived, setShowArchived] = useState(false)
  // 'add', or the variety being archived or restored: only that button says so.
  const [busyOn, setBusyOn] = useState<string | null>(null)
  const busy = busyOn !== null
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  // A layer (h 425-458): Escape and × close it, but not while a write is out
  // (h 3984-3992).
  const dialogEl = useRef<HTMLDivElement>(null)
  const { requestClose, confirm } = useLayer(true, onClose, { el: dialogEl, modal: true, busy })

  const load = async () => {
    const r = await apiJson<{ site: { id: string; name: string; archived_at: string | null }; varieties: SiteVariety[] }>(user, `/api/sites/${siteId}/varieties`)
    setSite(r.site)
    setList(r.varieties)
  }
  useEffect(() => {
    load().catch((e) => setError(err(e, 'Could not load the variety list')))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, siteId])

  const live = (list || []).filter((v) => !v.archived_at)
  const archived = (list || []).filter((v) => v.archived_at)
  const trimmed = name.trim()
  const same = live.find((v) => v.name.toLowerCase() === trimmed.toLowerCase())
  const blocked = !trimmed ? '' : same ? `${same.name} is already on the list` : trimmed.length > 120 ? 'A variety name is at most 120 characters' : ''
  const readOnly = !!site?.archived_at
  const uses = (v: SiteVariety) => `${plural(v.rows, 'row', 'rows')} · ${plural(v.jobs, 'job', 'jobs')}`

  const run = async (on: string, fn: () => Promise<string>) => {
    if (busy) return
    setBusyOn(on)
    setError('')
    setNotice('')
    try {
      const message = await fn()
      await load()
      setNotice(message)
      onChanged?.()
    } catch (e: any) {
      setError(err(e, 'Nothing was changed'))
    } finally {
      setBusyOn(null)
    }
  }
  const add = () =>
    !trimmed || blocked
      ? undefined
      : run('add', async () => {
          const r = await apiJson<{ variety: SiteVariety }>(user, `/api/sites/${siteId}/varieties`, { method: 'POST', body: { name: trimmed } })
          setName('')
          return `${r.variety.name} added. It is offered for new rows, replants and jobs on this Site.`
        })
  const archive = (v: SiteVariety) =>
    run(v.id, async () => {
      const r = await apiJson<{ variety: SiteVariety }>(user, `/api/site-varieties/${v.id}/archive`, { method: 'POST', body: {} })
      const kept = r.variety.rows || r.variety.jobs ? ` ${uses(r.variety)} keep it.` : ''
      return `${v.name} archived: no longer offered for new work.${kept}`
    })
  const restore = (v: SiteVariety) =>
    run(v.id, async () => {
      await apiJson(user, `/api/site-varieties/${v.id}/restore`, { method: 'POST', body: {} })
      return `${v.name} restored: offered for new work again.`
    })

  const Row = ({ v }: { v: SiteVariety }) => (
    <div className={v.archived_at ? `${m.variety} ${m.varietyOff}` : m.variety} data-testid="variety-row">
      <span className={m.varietyName}>
        {v.name}
        {v.archived_at ? ' (archived)' : ''}
      </span>
      <span className={m.varietyUse}>{uses(v)}</span>
      {!readOnly && (
        <PendingButton
          type="button"
          className={m.pinBtn}
          busy={busyOn === v.id}
          disabled={busy}
          pending={v.archived_at ? 'Restoring…' : 'Archiving…'}
          aria-label={`${v.archived_at ? 'Restore' : 'Archive'} ${v.name}`}
          onClick={() => (v.archived_at ? restore(v) : archive(v))}
        >
          {v.archived_at ? 'Restore' : 'Archive'}
        </PendingButton>
      )}
    </div>
  )

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={dialogEl} className={`${o.modal} ${o.wide}`} role="dialog" aria-label="Varieties" onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{site ? `${site.name}: varieties` : 'Varieties'}</h2>
            <p className={o.sub}>
              The exact products and varieties grown here. Rows, replants and picking jobs choose from this list, so every record names the variety the same way. A name is never edited: rows, jobs and
              everything after them carry it. If one is mistyped, archive it and add the right one.
            </p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          {readOnly && <div className={o.note}>This Site is archived, so its list is read-only.</div>}
          {!readOnly && (
            <div className={m.varietyAdd}>
              <input
                className={o.input}
                placeholder="e.g. Kiwifruit, Hayward"
                aria-label="New variety name"
                value={name}
                disabled={busy}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') add()
                }}
              />
              <PendingButton type="button" className={o.primary} busy={busyOn === 'add'} pending="Adding…" disabled={busy || !trimmed || !!blocked} onClick={add}>
                + Add variety
              </PendingButton>
            </div>
          )}
          {blocked && <div className={o.footNote}>{blocked}</div>}

          {!list ? (
            !error && <div className={m.status}>Loading…</div>
          ) : (
            <div className={m.varieties} aria-label="Variety list">
              {live.length ? live.map((v) => <Row key={v.id} v={v} />) : <div className={m.varietyNone}>No varieties on this Site yet. Add the first above.</div>}
              {showArchived && archived.map((v) => <Row key={v.id} v={v} />)}
            </div>
          )}
          {archived.length > 0 && (
            <div className={o.toggleRow}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle} id="variety-show-archived">
                  Show archived ({archived.length})
                </div>
              </div>
              <button type="button" role="switch" aria-checked={showArchived} aria-labelledby="variety-show-archived" className={o.switch} onClick={() => setShowArchived(!showArchived)} />
            </div>
          )}

          {notice && (
            <p className={m.status} role="status">
              {notice}
            </p>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} onClick={requestClose}>
            Done
          </button>
        </div>
        <Failed text={error} />
      </div>
      {confirm}
    </div>
  )
}

export function SiteMenu({
  user,
  sites,
  siteId,
  onPick,
  onAdded,
  onSettings,
  onVarietiesChanged,
}: {
  user: any
  sites: SiteSummary[]
  siteId: string
  onPick: (id: string) => void
  onAdded: (s: SiteRecord) => void
  onSettings: () => void
  // The variety list changed: a page that offers it reads it again.
  onVarietiesChanged?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [varieties, setVarieties] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = sites.find((s) => s.id === siteId)

  // The menu is a layer (h 425-458): Escape closes it, as a click outside
  // does; a half-typed name goes with it.
  useLayer(open, () => setOpen(false))
  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', down)
    return () => document.removeEventListener('mousedown', down)
  }, [open])

  return (
    <div className={m.bar}>
      <div className={m.wrap} ref={ref}>
        <button type="button" className={m.trigger} aria-haspopup="dialog" aria-expanded={open} aria-label={`Site: ${current?.name || 'none chosen'}`} onClick={() => setOpen(!open)}>
          <svg className={m.pinIcon} viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className={m.triggerName}>{current?.name || 'Choose a site'}</span>
          {current?.archived_at && <span className={m.tag}>Archived</span>}
          <svg className={m.caret} viewBox="0 0 24 24" aria-hidden="true">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        {open && (
          <div className={m.pop} role="dialog" aria-label="Sites">
            <div className={m.list}>
              {sites.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={s.archived_at ? m.itemOff : m.item}
                  aria-current={s.id === siteId ? 'true' : undefined}
                  onClick={() => {
                    setOpen(false)
                    if (s.id !== siteId) onPick(s.id)
                  }}
                >
                  <span className={m.tick} aria-hidden="true">
                    {s.id === siteId ? '✓' : ''}
                  </span>
                  <span className={m.itemText}>
                    <span className={m.itemName}>{s.name}</span>
                    <span className={m.itemMeta}>{siteLine(s)}</span>
                  </span>
                </button>
              ))}
            </div>
            <AddSiteForm
              user={user}
              onAdded={(s) => {
                setOpen(false)
                onAdded(s)
              }}
            />
          </div>
        )}
      </div>
      <button type="button" className={m.cog} title="Level names, mapping level, address, size and grower PIN" disabled={!current} onClick={onSettings}>
        <Gear /> Site settings
      </button>
      <button type="button" className={m.listBtn} title="The exact products and varieties grown on this Site" disabled={!current} onClick={() => setVarieties(true)}>
        <svg className={m.listIcon} viewBox="0 0 24 24" aria-hidden="true">
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
          <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
        </svg>
        Varieties
      </button>
      {varieties && current && <VarietiesDialog user={user} siteId={current.id} onClose={() => setVarieties(false)} onChanged={onVarietiesChanged} />}
    </div>
  )
}

interface Form {
  name: string
  address: string
  size: string
  pin: string
  weight: string
  binKg: string
  rate: string
  archived: boolean
  // '' is "Inherits from tenant".
  timezone: string
  l1: string
  l2: string
  l3: string
  showL2: boolean
  mapLevel: number
  region: string
  lat: string
  lng: string
  zoom: string
}

export type Settings = {
  name: string
  address: string | null
  size_hectares: number | null
  timezone: string | null
  grower_pin: string | null
  estimated_item_weight_g: number
  estimated_bin_weight_g: number
  level1_label: string
  level2_label: string
  level3_label: string
  show_level2: boolean
  map_level: number
  archived: boolean
  region: string | null
  map_centre: MapCentre | null
  map_zoom: number | null
  default_rate_per_kg: number | null
}

const formOf = (s: SiteRecord): Form => ({
  name: s.name,
  address: s.address || '',
  size: hectares(s.size_hectares) === null ? '' : String(hectares(s.size_hectares)),
  pin: s.grower_pin || '',
  // 0 is "not set" (013's default).
  weight: s.estimated_item_weight_g ? String(s.estimated_item_weight_g) : '',
  // Kept in grams, shown in kg.
  binKg: s.estimated_bin_weight_g ? String(s.estimated_bin_weight_g / 1000) : '',
  rate: s.default_rate_per_kg === null || s.default_rate_per_kg === undefined ? '' : Number(s.default_rate_per_kg).toFixed(2),
  archived: !!s.archived_at,
  timezone: s.timezone || '',
  l1: s.level1_label,
  l2: s.level2_label,
  l3: s.level3_label,
  showL2: s.show_level2 !== false,
  mapLevel: s.map_level,
  region: s.region || '',
  lat: s.map_centre ? String(s.map_centre.lat) : '',
  lng: s.map_centre ? String(s.map_centre.lng) : '',
  zoom: s.map_zoom === null || s.map_zoom === undefined ? '' : String(s.map_zoom),
})

// Six places, as 083 holds it, so what is sent compares with what comes back.
const degrees = (v: string) => (v.trim() === '' ? null : Number(Number(v.trim()).toFixed(6)))

// The form as the request sends it, and the first reason it cannot be sent.
export function readSettings(f: Form): { values: Settings; blocked: string } {
  const size = f.size.trim()
  const weight = f.weight.trim()
  const binKg = f.binKg.trim()
  const lat = degrees(f.lat)
  const lng = degrees(f.lng)
  const zoom = f.zoom.trim()
  const rate = f.rate.trim().replace(/^\$/, '')
  const centreBlocked =
    (lat === null) !== (lng === null)
      ? 'Give the map centre a latitude and a longitude, or leave both blank'
      : lat !== null && !(Number.isFinite(lat) && Math.abs(lat) <= 90)
        ? 'Latitude is a number from -90 to 90'
        : lng !== null && !(Number.isFinite(lng) && Math.abs(lng) <= 180)
          ? 'Longitude is a number from -180 to 180'
          : ''
  const values: Settings = {
    name: f.name.trim(),
    address: f.address.trim() || null,
    size_hectares: size === '' ? null : Number(size),
    timezone: f.timezone || null,
    grower_pin: f.pin.trim() || null,
    estimated_item_weight_g: weight === '' ? 0 : Number(weight),
    estimated_bin_weight_g: binKg === '' ? 0 : Math.round(Number(binKg) * 1000),
    level1_label: f.l1.trim(),
    level2_label: f.l2.trim(),
    level3_label: f.l3.trim(),
    show_level2: f.showL2,
    map_level: f.mapLevel,
    archived: f.archived,
    region: f.region.trim() || null,
    map_centre: lat !== null && lng !== null && !centreBlocked ? { lat, lng } : null,
    map_zoom: zoom === '' ? null : Number(zoom),
    default_rate_per_kg: rate === '' ? null : Number(rate),
  }
  const blocked = !values.name
    ? 'Give the site a name'
    : !values.level1_label || !values.level2_label || !values.level3_label
      ? 'Each level needs a name'
      : values.size_hectares !== null && !(Number.isFinite(values.size_hectares) && values.size_hectares >= 0)
        ? 'Size is a number of hectares, or blank'
        : values.grower_pin && !/^[0-9]{1,6}$/.test(values.grower_pin)
          ? 'A grower PIN is 1 to 6 digits'
          : !(Number.isInteger(values.estimated_item_weight_g) && values.estimated_item_weight_g >= 0)
            ? 'Estimated item weight is a whole number of grams'
            : binKg !== '' && !(/^\d+(\.\d{1,3})?$/.test(binKg) && values.estimated_bin_weight_g <= 5000000)
              ? 'Estimated bin weight is kg, up to 5000, or blank'
            : values.default_rate_per_kg !== null && !(/^\d+(\.\d{1,2})?$/.test(rate) && values.default_rate_per_kg > 0)
              ? 'The default contract rate is dollars and cents per kg, more than $0.00, or blank'
              : centreBlocked ||
              (values.map_zoom !== null && !(Number.isInteger(values.map_zoom) && values.map_zoom >= 0 && values.map_zoom <= 22)
                ? 'The default zoom is a whole number from 0 to 22'
                : '')
  return { values, blocked }
}

// Only what changed is sent, with what it was when the form opened: a save
// made after someone else's is refused (STALE) rather than overwriting it.
export function settingsChange(opened: Settings, now: Settings): Partial<Settings> & { before?: Partial<Settings> } {
  const keys = (Object.keys(now) as (keyof Settings)[]).filter((k) => JSON.stringify(now[k]) !== JSON.stringify(opened[k]))
  if (!keys.length) return {}
  return { ...Object.fromEntries(keys.map((k) => [k, now[k]])), before: Object.fromEntries(keys.map((k) => [k, opened[k]])) }
}

const FIELD_NAME: Record<string, string> = {
  name: 'the name',
  address: 'the address',
  size_hectares: 'the size',
  timezone: 'the time zone',
  grower_pin: 'the grower PIN',
  estimated_item_weight_g: 'the estimated item weight',
  estimated_bin_weight_g: 'the estimated bin weight',
  default_rate_per_kg: 'the default contract rate',
  level1_label: 'level 1',
  level2_label: 'level 2',
  level3_label: 'level 3',
  show_level2: 'level 2',
  map_level: 'the mapping level',
  archived: 'the archive setting',
  region: 'the region',
  map_centre: 'the map centre',
  map_zoom: 'the default zoom',
}

const digits = (v: string, max: number) => v.replace(/\D/g, '').slice(0, max)

export function SiteSettingsDialog({ user, siteId, onClose, onSaved }: { user: any; siteId: string; onClose: () => void; onSaved: (s: SiteRecord, message: string) => void }) {
  const [site, setSite] = useState<SiteRecord | null>(null)
  const [form, setForm] = useState<Form | null>(null)
  const [locked, setLocked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  // A layer (h 425-458): Escape and × close it, with unsaved changes ask first.
  const dialogEl = useRef<HTMLDivElement>(null)

  // Fresh from the server, not the list: the lock and the stale check need it.
  useEffect(() => {
    let live = true
    apiJson<SiteRecord>(user, `/api/sites/${siteId}`)
      .then((s) => {
        if (!live) return
        setSite(s)
        setForm(formOf(s))
        setLocked(!!s.timezone_locked)
      })
      .catch((e) => live && setError(err(e, 'Could not load the site settings')))
    return () => {
      live = false
    }
  }, [user, siteId])

  // "Changing the level replaces the map, and says so first: how many
  // polygons go, and how many targets the new level has to draw." Read from
  // the server for the level chosen; the save confirms that count.
  const [impact, setImpact] = useState<MappingImpact | null>(null)
  const chosenLevel = form?.mapLevel
  const savedLevel = site?.map_level
  useEffect(() => {
    setImpact(null)
    if (chosenLevel === undefined || chosenLevel === savedLevel) return
    let live = true
    apiJson<MappingImpact>(user, `/api/sites/${siteId}/mapping-impact?level=${chosenLevel}`)
      .then((r) => live && setImpact(r))
      .catch((e) => live && setError(err(e, 'Could not read what changing the mapping level removes')))
    return () => {
      live = false
    }
  }, [user, siteId, chosenLevel, savedLevel])

  const title = 'Site settings'
  const sub = `What the three levels are called in ${site?.name || 'this site'}, and the one level its boundaries are drawn at. Depth is always three: only the names change.`

  // Unsaved is what a save would send (h 425-458): the load arriving, or a
  // STALE refusal putting back what is saved, is not a change.
  const loaded = !!site && !!form
  const unsaved = loaded && Object.keys(settingsChange(readSettings(formOf(site)).values, readSettings(form).values)).length > 0
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: unsaved && !busy, el: dialogEl, modal: true, focusFirst: loaded, busy })
  const slow = useSlow(busy)

  if (!site || !form) {
    return (
      <div className={o.scrim} onClick={requestClose}>
        <div ref={dialogEl} className={`${o.modal} ${o.wide}`} role="dialog" aria-label="Site settings" onClick={(e) => e.stopPropagation()}>
          <div className={o.head}>
            <div className={o.headText}>
              <h2 className={o.title}>{title}</h2>
              <p className={o.sub}>{sub}</p>
            </div>
            <button type="button" className={o.close} aria-label="Close" disabled={true} onClick={requestClose}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className={o.body}>
            {error ? (
              <div className={o.danger} role="alert">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01" />
                </svg>
                <span>{error}</span>
              </div>
            ) : (
              <div className={m.status}>Loading…</div>
            )}
          </div>
        </div>
        {confirm}
      </div>
    )
  }

  const opened = readSettings(formOf(site)).values
  const { values, blocked } = readSettings(form)
  const change = settingsChange(opened, values)
  const changed = Object.keys(change).length > 0
  const set = (patch: Partial<Form>) => setForm({ ...form, ...patch })
  const L = [form.l1.trim() || 'Level 1', form.l2.trim() || 'Level 2', form.l3.trim() || 'Level 3']
  const low = (s: string) => s.toLowerCase()
  // Blank inherits the tenant's operating zone (§9, "Time zone: two zones,
  // one clock for money"). Once work is recorded here only a choice that
  // keeps the clock its captures are read on is offered (SITE-1): its own
  // zone, or inheriting a tenant zone that is the same.
  const tenantZone = site.tenant_timezone || null
  const effective = site.effective_timezone || site.timezone || tenantZone || 'UTC'
  const keeps = (tz: string) => (tz || tenantZone) === effective
  const zones = locked ? ['', effective].filter(keeps) : ['', ...zoneOptions(site.timezone)]
  const fixed = locked && zones.length < 2

  const levelChange = 'map_level' in change
  const mapBlocked = levelChange && impact?.level !== values.map_level ? 'Reading what the new mapping level removes…' : ''

  const save = async () => {
    if (busy || blocked || mapBlocked || !changed) return
    setBusy(true)
    setError('')
    try {
      const body = levelChange ? { ...change, boundaries_removed: impact!.boundaries } : change
      const s = await apiJson<SiteRecord>(user, `/api/sites/${site.id}`, { method: 'PUT', body })
      const message =
        'show_level2' in change
          ? values.show_level2
            ? `${L[1]} is shown again.`
            : `${L[1]} is hidden. Nothing was deleted. Every ${low(L[2])} keeps its place.`
          : 'archived' in change
            ? values.archived
              ? `${values.name} archived. It is no longer offered for new picking jobs, teams or orders.`
              : `${values.name} is active again.`
            : levelChange
              ? `${values.name} is mapped by ${L[values.map_level - 1].toLowerCase()} now${impact?.boundaries ? `; ${plural(impact.boundaries, 'boundary', 'boundaries')} removed` : ''}.`
              : `${values.name} settings saved.`
      onSaved(s, message)
    } catch (e: any) {
      const code = e instanceof ApiError ? e.body?.code : null
      if (code === 'STALE' && e.body.site) {
        const fields = [...new Set((e.body.fields || []).map((k: string) => FIELD_NAME[k] || k))]
        setSite({ ...e.body.site, timezone_locked: locked })
        setForm(formOf(e.body.site))
        setError(`Someone else changed ${fields.join(', ') || 'these settings'} since you opened them. The form now shows what is saved; make your change again.`)
      } else if (code === 'BOUNDARIES_WOULD_BE_REMOVED') {
        // Someone drew or removed a boundary since the preview was read: show
        // the count the server holds now, and let them confirm that one.
        setImpact({ level: values.map_level, current_level: site.map_level, boundaries: e.body.boundaries, targets: e.body.targets })
        setError(err(e, 'The mapping level was not changed'))
      } else if (code === 'TIMEZONE_LOCKED') {
        setLocked(true)
        setForm({ ...form, timezone: site.timezone || '' })
        setError(err(e, 'The time zone was not changed'))
      } else {
        setError(err(e, 'The settings were not saved'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={dialogEl} className={`${o.modal} ${m.settings}`} role="dialog" aria-label="Site settings" onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>{title}</h2>
            <p className={o.sub}>{sub}</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" disabled={busy} onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={o.body}>
          <div className={`${m.stack} ${inertClass(busy, slow)}`}>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-name">
                Site name
              </label>
              <input id="ss-name" className={o.input} value={form.name} onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-address">
                Address
              </label>
              <input id="ss-address" className={o.input} value={form.address} onChange={(e) => set({ address: e.target.value })} />
            </div>
            <div className={o.grid2}>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="ss-region">
                  Region
                </label>
                <input id="ss-region" className={o.input} placeholder="Not set" value={form.region} onChange={(e) => set({ region: e.target.value })} />
              </div>
              <div className={o.field}>
                <label className={o.fieldLabel} htmlFor="ss-size">
                  Size (hectares)
                </label>
                <input id="ss-size" className={o.input} inputMode="decimal" placeholder="Not set" value={form.size} onChange={(e) => set({ size: e.target.value })} />
              </div>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-pin">
                Grower PIN
              </label>
              <input id="ss-pin" className={o.input} inputMode="numeric" placeholder="Not set" value={form.pin} onChange={(e) => set({ pin: digits(e.target.value, 6) })} />
              <span className={o.fieldHint}>
                Inherited by every {low(L[0])}, {low(L[1])} and {low(L[2])} on this site. A {low(L[0])} can be given its own PIN, which flows down to everything inside it; a {low(L[1])} or{' '}
                {low(L[2])} cannot.
              </span>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-weight">
                Estimated item weight before weighing (g)
              </label>
              <input id="ss-weight" className={o.input} inputMode="numeric" placeholder="Not set" value={form.weight} onChange={(e) => set({ weight: digits(e.target.value, 9) })} />
              <span className={o.fieldHint}>
                Grams. What an item is assumed to hold before it is weighed. A self-scan or Crew receipt takes this weight, and it counts in wages, credits and invoices like any weight,
                marked as estimated, until a weighing replaces it. Without it, a cycle still unweighed at a payroll export counts 0 kg and is flagged there.
              </span>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-bin-weight">
                Estimated bin weight before weighing (kg)
              </label>
              <input
                id="ss-bin-weight"
                className={o.input}
                inputMode="decimal"
                placeholder="Not set"
                value={form.binKg}
                onChange={(e) => set({ binKg: e.target.value.replace(/[^\d.]/g, '').slice(0, 8) })}
              />
              <span className={o.fieldHint}>
                The fruit in a full bin here. With Shared bins in Self Scan on (Teams, settings cog), a bin scanned by several pickers is assumed to hold this until it is weighed, shared
                equally among them. A bin tag's own full weight, set in God Mode's Tags, comes first.
              </span>
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-rate">
                Default contract rate ($/kg)
              </label>
              <input id="ss-rate" className={o.input} inputMode="decimal" placeholder="Not set" value={form.rate} onChange={(e) => set({ rate: e.target.value })} />
              <span className={o.fieldHint}>
                A new picking job here starts with this rate, from the day the job is made. It can be changed when the job is created, and changing it here changes no existing job.
              </span>
            </div>
            <div className={`${o.toggleRow} ${m.cardRow}`}>
              <div className={o.toggleText}>
                <div className={o.toggleTitle} id="ss-archived">
                  Archived
                </div>
                <div className={o.toggleSub}>
                  {form.archived
                    ? 'Archived: this site is not offered for new picking jobs, teams or orders. Its history, boundaries and past jobs stay exactly as they are.'
                    : 'Active: offered wherever a site is chosen.'}
                </div>
              </div>
              <button type="button" role="switch" aria-checked={form.archived} aria-labelledby="ss-archived" className={o.switch} onClick={() => set({ archived: !form.archived })} />
            </div>
            <div className={o.field}>
              <label className={o.fieldLabel} htmlFor="ss-tz">
                Time zone
              </label>
              <select id="ss-tz" className={o.input} value={form.timezone} disabled={fixed} onChange={(e) => set({ timezone: e.target.value })}>
                {zones.map((z) =>
                  z ? (
                    <option key={z} value={z}>
                      {z}
                    </option>
                  ) : (
                    // With no tenant zone there is nothing to inherit: the API
                    // refuses the switch (TENANT_TIMEZONE_UNSET).
                    <option key="inherit" value="" disabled={!tenantZone && site.timezone !== null}>
                      Inherits the subscription zone ({tenantZone || 'not set yet'})
                    </option>
                  )
                )}
              </select>
              <span className={o.fieldHint}>
                {locked
                  ? 'Work is already recorded at this site, so the zone its captures are read in is fixed: what a change would do to the days already recorded is not settled yet (SITE-1).'
                  : 'Decides which day a capture here falls on. Set it only when this site sits in another zone from the rest of the subscription. It can be changed until work is recorded at this site.'}
                {locked && !fixed ? ` It can still move between its own ${effective} and inheriting the subscription zone, which is the same clock.` : ''}
                {` Read in ${effective} now${site.zone ? ` (${offsetText(site.zone)})` : ''}.`}
                {!tenantZone
                  ? site.timezone === null
                    ? ' This workspace has no operating time zone yet, so captures here are read as UTC until Dataphyll sets one in Client Setup.'
                    : ' This workspace has no operating time zone yet (Dataphyll sets it in Client Setup), so a site needs its own.'
                  : ''}
              </span>
            </div>

            <div className={m.group}>
              <div className={o.label}>Levels</div>
              {!form.showL2 && <p className={m.intro}>The data still holds three levels: {L[1]} is simply hidden from the interface.</p>}
              <div className={m.levels}>
                {(
                  [
                    ['l1', 'Level 1'],
                    ['l2', 'Level 2'],
                    ['l3', 'Level 3: the final level'],
                  ] as const
                ).map(([k, label]) => (
                  <div key={k} className={o.field}>
                    <label className={o.fieldLabel} htmlFor={`ss-${k}`}>
                      {label}
                    </label>
                    <input id={`ss-${k}`} className={o.input} value={form[k]} onChange={(e) => set({ [k]: e.target.value } as Partial<Form>)} />
                  </div>
                ))}
              </div>
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle} id="ss-hide-l2">
                    Hide level 2 in the interface
                  </div>
                  <div className={o.toggleSub}>
                    {form.showL2
                      ? 'Hiding it shows two levels everywhere in the interface. The third level stays in the data.'
                      : `Existing ${low(L[1])}s are kept and still hold their ${low(L[2])}s. Nothing is deleted or moved.`}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!form.showL2}
                  aria-labelledby="ss-hide-l2"
                  className={o.switch}
                  onClick={() => {
                    const hide = form.showL2
                    // A hidden level cannot be the mapping level.
                    set({ showL2: !hide, mapLevel: hide && form.mapLevel === 2 ? 1 : form.mapLevel })
                  }}
                />
              </div>
            </div>

            <div className={m.group}>
              <div className={o.label} id="ss-map">
                Mapping level
              </div>
              <div className={m.choices} role="radiogroup" aria-labelledby="ss-map">
                {(form.showL2 ? [1, 2, 3] : [1, 3]).map((n) => {
                  const on = form.mapLevel === n
                  return (
                    <label key={n} className={on ? `${o.choice} ${o.choiceOn}` : o.choice}>
                      <input className={m.hiddenRadio} type="radio" name="ss-map" checked={on} onChange={() => set({ mapLevel: n })} />
                      <span className={o.radio} aria-hidden="true" />
                      <span className={m.choiceText}>{L[n - 1]}</span>
                    </label>
                  )
                })}
              </div>
              <span className={o.fieldHint}>The one level whose boundaries are drawn on the map.</span>
              {levelChange && impact?.level === values.map_level && (
                <div className={o.danger} data-testid="mapping-impact">
                  <span>{mappingImpactText(impact, L)}</span>
                </div>
              )}
            </div>

            <div className={m.group}>
              <div className={o.label}>Map</div>
              <p className={m.intro}>
                Where {site.name}'s map opens: a centre in decimal degrees, for example -39.6255 and 176.7668, and the zoom it opens at. Blank means not set yet, and the map is never opened on a guessed
                place.
              </p>
              <div className={m.three}>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="ss-lat">
                    Latitude
                  </label>
                  <input id="ss-lat" className={o.input} inputMode="decimal" placeholder="Not set" value={form.lat} onChange={(e) => set({ lat: e.target.value })} />
                </div>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="ss-lng">
                    Longitude
                  </label>
                  <input id="ss-lng" className={o.input} inputMode="decimal" placeholder="Not set" value={form.lng} onChange={(e) => set({ lng: e.target.value })} />
                </div>
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="ss-zoom">
                    Default zoom
                  </label>
                  <input id="ss-zoom" className={o.input} inputMode="numeric" placeholder="Not set" value={form.zoom} onChange={(e) => set({ zoom: digits(e.target.value, 2) })} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          {(blocked || mapBlocked) && <span className={o.footNote}>{blocked || mapBlocked}</span>}
          <PendingButton className={o.primary} busy={busy} pending="Saving…" disabled={!!blocked || !!mapBlocked || !changed} onClick={save}>
            {levelChange && impact?.boundaries ? `Save and remove ${plural(impact.boundaries, 'boundary', 'boundaries')}` : 'Save settings'}
          </PendingButton>
        </div>
        <Failed text={error} />
      </div>
      {confirm}
    </div>
  )
}
